import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { FileText, AlertTriangle, CheckCircle2, Upload } from 'lucide-react';

const FitnessClaimsHistory = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Claims History</CardTitle>
            <CardDescription className="text-white/60">
              Tell us about your business's claims history
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Claims History Question */}
          <div>
            <Label className="text-base font-semibold">
              Has your business had any of the following in the last 5 years?
            </Label>
            <ul className="text-sm text-slate-600 mt-2 mb-4 space-y-1 list-disc list-inside">
              <li>Liability claims</li>
              <li>Civil lawsuits</li>
              <li>Incidents resulting in injury</li>
              <li>Been non-renewed or cancelled by an insurer</li>
              <li>Sexual abuse or molestation incidents or claims</li>
            </ul>

            <div className="grid grid-cols-2 gap-3">
              <div
                data-testid="claims-no"
                onClick={() => updateFormData({ claims_history_flag: false, claims_notes: '' })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.claims_history_flag === false
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className={`h-5 w-5 ${
                      formData.claims_history_flag === false ? 'text-green-600' : 'text-slate-400'
                    }`} />
                    <span className={`font-medium ${
                      formData.claims_history_flag === false ? 'text-green-900' : 'text-slate-700'
                    }`}>
                      No
                    </span>
                  </div>
                </div>
              </div>

              <div
                data-testid="claims-yes"
                onClick={() => updateFormData({ claims_history_flag: true })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.claims_history_flag === true
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`h-5 w-5 ${
                      formData.claims_history_flag === true ? 'text-amber-600' : 'text-slate-400'
                    }`} />
                    <span className={`font-medium ${
                      formData.claims_history_flag === true ? 'text-amber-900' : 'text-slate-700'
                    }`}>
                      Yes
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Claims Details (if Yes) */}
          {formData.claims_history_flag && (
            <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Please provide details about any claims, lawsuits, or incidents. This information helps us provide you with an accurate quote.
                </p>
              </div>

              <div>
                <Label htmlFor="claims_notes">Description of Claims/Incidents *</Label>
                <Textarea
                  id="claims_notes"
                  data-testid="claims-notes-textarea"
                  value={formData.claims_notes}
                  onChange={(e) => updateFormData({ claims_notes: e.target.value })}
                  required={formData.claims_history_flag}
                  className="mt-1.5 min-h-[120px]"
                  placeholder="Please describe the nature of any claims, lawsuits, or incidents, including dates and outcomes if known..."
                />
              </div>

              <div>
                <Label>Upload Loss Runs / Documents (Optional)</Label>
                <p className="text-sm text-slate-600 mb-2">
                  If you have loss run reports or related documents, please upload them here
                </p>
                <div className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center bg-white">
                  <Upload className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    PDF, DOC, or image files up to 10MB
                  </p>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    className="hidden"
                    data-testid="claims-file-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* No Claims Confirmation */}
          {formData.claims_history_flag === false && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-sm text-green-800 font-medium">
                  Great! A clean claims history helps ensure favorable underwriting.
                </p>
              </div>
            </div>
          )}

          <ThemedButton 
            type="submit"
            data-testid="fitness-claims-next-btn"
            disabled={formData.claims_history_flag === undefined || (formData.claims_history_flag && !formData.claims_notes)}
            variant="wizard" size="full"
          >
            Next: Fraud Warranty
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessClaimsHistory;
